import { FC, useState } from 'react';
import { captureException } from '@sentry/react';
import { Avatar, Button, Col, Divider, Modal, Row } from 'antd';
import { TbArrowDown, TbArrowRight, TbFolderSymlink } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { FolderDestinationDropdown, WorkspaceResourceAvatar, WorkspaceResourceDropdown } from '@/shared';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import { PlatformForm as Form } from '@/shared/components/forms';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { getWorkspaceResourceTypeLabel } from '@/shared/components/workspace-resources';
import { useFolderStore } from '@/stores';
import { Folder, SetStateMethod } from '@/types';

interface MoveResourceToFolderModalProps {
    open: boolean;
    setOpen: SetStateMethod<boolean>;
    resource?: any;
    singularResource?: string;
}

const MoveResourceToFolderModal: FC<MoveResourceToFolderModalProps> = ({
    open,
    setOpen,
    resource,
    singularResource,
}): JSX.Element => {
    const [selectedResource, setSelectedResource] = useState<any>({});
    const [selectedFolder, setSelectedFolder] = useState<Folder | any>({});

    const { addResourcesToFolder } = useFolderStore();

    const { token } = useAntDesignTheme();

    const [createLoading, setCreateLoading] = useState(false);
    const resourceTypeLabel = singularResource ? getWorkspaceResourceTypeLabel(singularResource) : 'Workspace Resource';

    const resetForm = () => {
        setSelectedResource({});
        setSelectedFolder({});
    };

    const postResourceMigration = () => {
        const movedResourceName = selectedResource?.name || selectedResource?.title || selectedResource?.editedName;

        resetForm();
        setOpen(false);

        toast(`Moved to Folder`, {
            icon: <TbFolderSymlink size='1rem' />,
            description: movedResourceName,
        });
    };

    const handleClose = () => {
        if (createLoading) {
            return;
        }

        resetForm();
        setOpen(false);
    };

    const handleMoveResourceToFolder = async () => {
        try {
            setCreateLoading(true);

            await addResourcesToFolder(selectedFolder?.id, {
                [`${String(selectedResource?.type)?.toLowerCase()}s`]: [selectedResource],
            });
            postResourceMigration();
        } catch (error) {
            captureException(error);
            toast.error('Unable to move this resource to the selected folder.');
        } finally {
            setCreateLoading(false);
        }
    };

    return (
        <Modal
            afterOpenChange={(nextOpen) => {
                if (nextOpen) {
                    setSelectedResource(resource?.id ? resource : {});
                    setSelectedFolder({});
                    setCreateLoading(false);
                }
            }}
            open={open}
            onCancel={handleClose}
            mask={false}
            title={`Move to Folder`}
            footer={
                <Row style={{ width: '100%' }}>
                    <Col flex='none'>
                        <Button onClick={handleClose}>Cancel</Button>
                    </Col>
                    <Col flex='auto' />
                    <Col flex='none'>
                        <Button
                            type='primary'
                            loading={createLoading}
                            disabled={!selectedResource?.id || !selectedFolder?.id}
                            onClick={handleMoveResourceToFolder}
                        >
                            Move to Folder
                            <TbArrowRight />
                        </Button>
                    </Col>
                </Row>
            }
        >
            <Form layout='vertical'>
                <Form.Item style={{ marginBottom: 0 }} label={resourceTypeLabel}>
                    <WorkspaceResourceDropdown resource={selectedResource} setResource={setSelectedResource}>
                        <DropdownButton block style={{ justifyContent: 'flex-start' }}>
                            <Row style={{ width: '100%' }} align='middle'>
                                <Col flex='auto' style={{ textAlign: 'left' }}>
                                    <WorkspaceResourceAvatar
                                        listType={selectedResource?.listType ?? selectedResource?.type}
                                        resource={singularResource ?? selectedResource?.type ?? 'resource'}
                                        size={16}
                                        style={{ verticalAlign: '-1px', marginRight: '0.375rem' }}
                                        website={selectedResource?.website}
                                    />
                                    {selectedResource?.id ? (
                                        selectedResource?.name ||
                                        selectedResource?.title ||
                                        selectedResource?.editedName
                                    ) : (
                                        <span>Select Workspace Resource...</span>
                                    )}
                                </Col>
                            </Row>
                        </DropdownButton>
                    </WorkspaceResourceDropdown>
                </Form.Item>
                <Divider style={{ marginBottom: '0.5rem' }}>
                    <Avatar
                        size='small'
                        shape='square'
                        icon={<TbArrowDown size='75%' style={{ color: token.colorTextTertiary }} />}
                        style={{ border: `1px solid ${token.colorBorder}` }}
                    />
                </Divider>
                <Form.Item style={{ marginBottom: 0 }} label='Folder Destination'>
                    <FolderDestinationDropdown
                        destinationFolder={selectedFolder}
                        setDestinationFolder={setSelectedFolder}
                    >
                        <DropdownButton block style={{ justifyContent: 'flex-start' }}>
                            <Row style={{ width: '100%' }} align='middle'>
                                <Col flex='auto' style={{ textAlign: 'left' }}>
                                    <WorkspaceResourceAvatar
                                        active={Boolean(selectedFolder?.id)}
                                        resource='folder'
                                        size={16}
                                        style={{ verticalAlign: '-1px', marginRight: '0.375rem' }}
                                    />
                                    {selectedFolder?.id ? (
                                        selectedFolder?.name
                                    ) : (
                                        <span>Select Folder Destination...</span>
                                    )}
                                </Col>
                            </Row>
                        </DropdownButton>
                    </FolderDestinationDropdown>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default MoveResourceToFolderModal;
